import { useEffect, useState } from "react";
import { MessageCircle, X, ArrowUpRight, BadgeCheck } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { WA_DEFAULT, WA_DEMO } from "../../lib/site";
import { scrollToId } from "../../lib/scroll";
import { Magnetic } from "./Magnetic";

const POPUP_KEY = "se_popup_dismissed";

const ExitPopup = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(POPUP_KEY)) return;

    const show = () => {
      if (sessionStorage.getItem(POPUP_KEY)) return;
      sessionStorage.setItem(POPUP_KEY, "1");
      setOpen(true);
    };

    const onScroll = () => {
      const h = document.documentElement;
      if (h.scrollTop / (h.scrollHeight - h.clientHeight) >= 0.7) show();
    };
    const onLeave = (e) => {
      if (e.clientY <= 0 && window.matchMedia("(pointer: fine)").matches) show();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    document.documentElement.addEventListener("mouseleave", onLeave);
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.documentElement.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const close = () => setOpen(false);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="exit-popup"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-ink/85 backdrop-blur-md flex items-center justify-center p-5"
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label="Free demo offer"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative max-w-md w-full bg-panel border border-brand/40 p-8 sm:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="absolute top-0 left-0 w-full h-1 bg-brand" aria-hidden="true" />
            <button data-testid="exit-popup-close" onClick={close} aria-label="Close popup" className="absolute top-4 right-4 text-mute hover:text-white transition-colors">
              <X size={18} />
            </button>
            <p className="eyebrow mb-4">Before you go —</p>
            <h3 className="font-display font-light text-3xl sm:text-4xl text-white leading-tight" data-testid="exit-popup-headline">
              Want a <em className="italic text-brand">free demo website</em> built with your business info?
            </h3>
            <p className="mt-4 text-mute text-sm leading-relaxed">
              No advance. No obligation. You only pay if you love it.
            </p>
            <a
              data-testid="exit-popup-claim-button"
              href={WA_DEMO}
              target="_blank"
              rel="noopener noreferrer"
              onClick={close}
              className="btn-lime group mt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-4 font-semibold text-sm"
            >
              Claim My Free Demo <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
            </a>
            <button data-testid="exit-popup-dismiss" onClick={close} className="mt-3 w-full text-center text-mute hover:text-white text-xs transition-colors py-2">
              No thanks, I&apos;ll look around
            </button>
            <p className="mt-4 flex items-center justify-center gap-2 text-[11px] text-mute">
              <BadgeCheck size={13} className="text-brand" /> No advance until you approve the demo
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const Conversion = () => (
  <>
    <Magnetic className="fixed z-40 right-4 sm:right-6 bottom-24 md:bottom-6">
      <a
        data-testid="whatsapp-floating-button"
        href={WA_DEFAULT}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        className="relative flex items-center justify-center w-14 h-14 rounded-full bg-brand text-ink shadow-[0_0_35px_rgba(204,255,2,0.45)]"
      >
        <span className="absolute inset-0 rounded-full bg-brand animate-ping opacity-25" aria-hidden="true" />
        <MessageCircle size={24} className="relative" />
      </a>
    </Magnetic>

    <div data-testid="mobile-sticky-bar" className="fixed bottom-0 inset-x-0 z-40 md:hidden grid grid-cols-2 border-t hairline bg-ink/95 backdrop-blur-lg">
      <a
        data-testid="mobile-bar-whatsapp"
        href={WA_DEFAULT}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 py-4 text-white text-sm font-semibold border-r hairline"
      >
        <MessageCircle size={16} className="text-brand" /> Chat on WhatsApp
      </a>
      <button
        data-testid="mobile-bar-demo"
        onClick={() => scrollToId("#contact")}
        className="flex items-center justify-center gap-2 py-4 bg-brand text-ink text-sm font-semibold"
      >
        Get Free Demo <ArrowUpRight size={15} />
      </button>
    </div>

    <ExitPopup />
  </>
);
